import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus,
  FiX,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiImage,
  FiCalendar,
  FiClock,
  FiUser,
  FiMail,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiSearch
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { adminBookingAPI, BookingService, BookingRequest, getBookingServiceImageURL } from '../services/api';
import './BookingManagement.css';
import './Dashboard.css';

interface BookingManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const BookingManagement: React.FC<BookingManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState<'services' | 'requests'>('services');
  const [services, setServices] = useState<BookingService[]>([]);
  const [requests, setRequests] = useState<BookingRequest[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [uploadingService, setUploadingService] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    title: '',
    description: '',
    is_active: true,
    image_file: null as File | null
  });
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<BookingRequest | null>(null);
  const [requestStatus, setRequestStatus] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'user' | 'service'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (activeTab === 'services') {
      fetchServices();
    } else {
      fetchRequests();
    }
  }, [activeTab, statusFilter]);

  // Fetch pending count on mount and when requests change
  useEffect(() => {
    const countPending = async () => {
      try {
        const data = await adminBookingAPI.listRequests('pending');
        setPendingCount(data.length);
      } catch (error) {
        console.error('Error fetching pending count:', error);
      }
    };
    countPending();
  }, []);

  useEffect(() => {
    if (requests.length > 0) {
      const pending = requests.filter(r => r.status === 'pending').length;
      setPendingCount(pending);
    }
  }, [requests]);

  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      const data = await adminBookingAPI.listServices();
      setServices(data);
    } catch (error: any) {
      console.error('Error fetching booking services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoadingRequests(true);
      const data = await adminBookingAPI.listRequests(statusFilter !== 'all' ? statusFilter : undefined);
      setRequests(data);
    } catch (error: any) {
      console.error('Error fetching booking requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Filter and sort requests
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...requests];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(request => 
        request.user_email.toLowerCase().includes(query) ||
        (request.user_name && request.user_name.toLowerCase().includes(query)) ||
        request.service_title.toLowerCase().includes(query)
      );
    }

    // Note: Status filter is already applied by API in fetchRequests, so we don't need to filter again here
    // This ensures consistency - API handles the filtering

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'user':
          const aName = a.user_name || a.user_email;
          const bName = b.user_name || b.user_email;
          comparison = aName.localeCompare(bName);
          break;
        case 'service':
          comparison = a.service_title.localeCompare(b.service_title);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [requests, searchQuery, statusFilter, sortBy, sortOrder]);

  const handleCreateService = async () => {
    if (!serviceForm.title.trim()) {
      alert('Title is required');
      return;
    }

    try {
      setUploadingService(true);
      const formData = new FormData();
      formData.append('title', serviceForm.title);
      formData.append('description', serviceForm.description);
      formData.append('is_active', serviceForm.is_active.toString());
      if (serviceForm.image_file) {
        formData.append('image_file', serviceForm.image_file);
      }

      if (editingServiceId) {
        await adminBookingAPI.updateService(editingServiceId, formData);
      } else {
        await adminBookingAPI.createService(formData);
      }

      resetServiceForm();
      setShowServiceModal(false);
      fetchServices();
    } catch (error: any) {
      console.error('Error saving booking service:', error);
      alert(error.message || 'Failed to save booking service');
    } finally {
      setUploadingService(false);
    }
  };

  const handleEditService = (service: BookingService) => {
    setServiceForm({
      title: service.title,
      description: service.description,
      is_active: service.is_active,
      image_file: null
    });
    setEditingServiceId(service.id);
    setShowServiceModal(true);
  };

  const handleDeleteService = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await adminBookingAPI.deleteService(id);
      fetchServices();
    } catch (error: any) {
      console.error('Error deleting booking service:', error);
      alert(error.message || 'Failed to delete booking service');
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    try {
      await adminBookingAPI.updateRequest(selectedRequest.id, {
        status: requestStatus || selectedRequest.status,
        admin_notes: adminNotes
      });
      setShowRequestModal(false);
      setSelectedRequest(null);
      setRequestStatus('');
      setAdminNotes('');
      // Refresh requests with current filter
      await fetchRequests();
      // Also refresh pending count
      const pendingData = await adminBookingAPI.listRequests('pending');
      setPendingCount(pendingData.length);
    } catch (error: any) {
      console.error('Error updating booking request:', error);
      alert(error.message || 'Failed to update booking request');
    }
  };

  const resetServiceForm = () => {
    setServiceForm({
      title: '',
      description: '',
      is_active: true,
      image_file: null
    });
    setEditingServiceId(null);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string): string => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: '#ffc107', icon: FiAlertCircle, text: 'Pending' },
      approved: { color: '#28a745', icon: FiCheckCircle, text: 'Approved' },
      rejected: { color: '#dc3545', icon: FiXCircle, text: 'Rejected' },
      cancelled: { color: '#6c757d', icon: FiXCircle, text: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className="status-badge" style={{ color: config.color, borderColor: config.color }}>
        {React.createElement(Icon as React.ComponentType<{ size?: number }>, { size: 14 })}
        <span>{config.text}</span>
      </span>
    );
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userEmail}
          organization="NAATI"
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="booking-management">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="page-title">Booking Management</h1>
            </motion.div>

            {/* Tabs */}
            <div className="tabs-container">
              <button
                className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
                onClick={() => setActiveTab('services')}
              >
                Services
              </button>
              <button
                className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                Requests ({pendingCount})
              </button>
            </div>

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="tab-content">
                <div className="dashboard-welcome">
                  <div>
                    <h2>Booking Services</h2>
                    <p>Manage available booking services</p>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      resetServiceForm();
                      setShowServiceModal(true);
                    }}
                  >
                    {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 18 })}
                    <span>Add Service</span>
                  </button>
                </div>

                {loadingServices ? (
                  <div className="loading-container">
                    {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                    <p>Loading services...</p>
                  </div>
                ) : services.length === 0 ? (
                  <div className="empty-state">
                    {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 48 })}
                    <p>No booking services found</p>
                    <button className="btn-primary" onClick={() => setShowServiceModal(true)}>
                      Add Your First Service
                    </button>
                  </div>
                ) : (
                  <div className="services-grid">
                    {services.map((service) => (
                      <motion.div
                        key={service.id}
                        className="service-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -5 }}
                      >
                        <div className="service-image">
                          {service.image_garage_key ? (
                            <img src={getBookingServiceImageURL(service.id)} alt={service.title} />
                          ) : (
                            <div className="service-image-placeholder">
                              {React.createElement(FiImage as React.ComponentType<{ size?: number }>, { size: 48 })}
                            </div>
                          )}
                          {!service.is_active && (
                            <div className="service-inactive-badge">Inactive</div>
                          )}
                        </div>
                        <div className="service-info">
                          <h3>{service.title}</h3>
                          <p className="service-description">{service.description}</p>
                          <div className="service-actions">
                            <button
                              className="btn-icon"
                              onClick={() => handleEditService(service)}
                              title="Edit"
                            >
                              {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 18 })}
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleDeleteService(service.id)}
                              title="Delete"
                            >
                              {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 18 })}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="tab-content">
                <div className="dashboard-welcome">
                  <div>
                    <h2>Booking Requests</h2>
                    <p>Review and manage booking requests</p>
                  </div>
                </div>

                {/* Filters and Search */}
                <div className="requests-table-filters">
                  <div className="filter-group">
                    <div className="search-input-wrapper">
                      <span className="search-icon">
                        {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                      </span>
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  </div>
                  <div className="filter-group">
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        // fetchRequests will be called automatically via useEffect when statusFilter changes
                      }}
                      className="filter-select"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'date' | 'user' | 'service')}
                      className="filter-select"
                    >
                      <option value="date">Sort by Date</option>
                      <option value="user">Sort by User</option>
                      <option value="service">Sort by Service</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <button
                      className={`sort-order-btn ${sortOrder === 'asc' ? 'active' : ''}`}
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                </div>

                {loadingRequests ? (
                  <div className="loading-container">
                    {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                    <p>Loading requests...</p>
                  </div>
                ) : filteredAndSortedRequests.length === 0 ? (
                  <div className="empty-state">
                    {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 48 })}
                    <p>No booking requests found</p>
                  </div>
                ) : (
                  <div className="requests-table-container">
                    <table className="requests-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Service</th>
                          <th>Preferred Date</th>
                          <th>Preferred Time</th>
                          <th>Message</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedRequests.map((request) => (
                          <tr key={request.id}>
                            <td>
                              <div className="user-info">
                                <span className="user-email">{request.user_email}</span>
                                {request.user_name && request.user_name !== request.user_email && (
                                  <span className="user-name">{request.user_name}</span>
                                )}
                              </div>
                            </td>
                            <td>{request.service_title}</td>
                            <td>{request.preferred_date ? formatDate(request.preferred_date) : '-'}</td>
                            <td>{request.preferred_time ? formatTime(request.preferred_time) : '-'}</td>
                            <td className="message-cell">{request.message || '-'}</td>
                            <td>{getStatusBadge(request.status)}</td>
                            <td>{formatDate(request.created_at)}</td>
                            <td>
                              <button
                                className="btn-icon"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setRequestStatus(request.status);
                                  setAdminNotes(request.admin_notes || '');
                                  setShowRequestModal(true);
                                }}
                                title="Review"
                              >
                                {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 18 })}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Modal */}
      <AnimatePresence>
        {showServiceModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !uploadingService && (setShowServiceModal(false), resetServiceForm())}
          >
            <motion.div
              className="modal-content booking-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingServiceId ? 'Edit Service' : 'Add Service'}</h2>
                <button
                  className="modal-close"
                  onClick={() => !uploadingService && (setShowServiceModal(false), resetServiceForm())}
                  disabled={uploadingService}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={serviceForm.title}
                    onChange={(e) => setServiceForm({ ...serviceForm, title: e.target.value })}
                    placeholder="Enter service title..."
                    disabled={uploadingService}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    placeholder="Enter description..."
                    rows={4}
                    disabled={uploadingService}
                  />
                </div>

                <div className="form-group">
                  <label>Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (!file.type.startsWith('image/')) {
                          alert('Please select an image file');
                          return;
                        }
                        if (file.size > 10 * 1024 * 1024) {
                          alert('Image file size must be less than 10MB');
                          return;
                        }
                        setServiceForm({ ...serviceForm, image_file: file });
                      }
                    }}
                    disabled={uploadingService}
                  />
                  {serviceForm.image_file && (
                    <div className="file-info">
                      {React.createElement(FiImage as React.ComponentType<{ size?: number }>, { size: 20 })}
                      <div>
                        <div className="file-name">{serviceForm.image_file.name}</div>
                        <div className="file-size">{(serviceForm.image_file.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={serviceForm.is_active}
                      onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })}
                      disabled={uploadingService}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowServiceModal(false);
                    resetServiceForm();
                  }}
                  disabled={uploadingService}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateService}
                  disabled={!serviceForm.title.trim() || uploadingService}
                >
                  {uploadingService ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      Saving...
                    </>
                  ) : (
                    editingServiceId ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Request Review Modal */}
      <AnimatePresence>
        {showRequestModal && selectedRequest && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRequestModal(false)}
          >
            <motion.div
              className="modal-content booking-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Review Booking Request</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowRequestModal(false)}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="request-details">
                  <div className="detail-row">
                    <span className="detail-label">User:</span>
                    <span className="detail-value">{selectedRequest.user_email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Service:</span>
                    <span className="detail-value">{selectedRequest.service_title}</span>
                  </div>
                  {selectedRequest.preferred_date && (
                    <div className="detail-row">
                      <span className="detail-label">Preferred Date:</span>
                      <span className="detail-value">{formatDate(selectedRequest.preferred_date)}</span>
                    </div>
                  )}
                  {selectedRequest.preferred_time && (
                    <div className="detail-row">
                      <span className="detail-label">Preferred Time:</span>
                      <span className="detail-value">{formatTime(selectedRequest.preferred_time)}</span>
                    </div>
                  )}
                  {selectedRequest.message && (
                    <div className="detail-row">
                      <span className="detail-label">Message:</span>
                      <span className="detail-value">{selectedRequest.message}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value">{getStatusBadge(selectedRequest.status)}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={requestStatus}
                    onChange={(e) => setRequestStatus(e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Admin Notes</label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowRequestModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleUpdateRequest}
                >
                  Update Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingManagement;

